/**
 * How-To Guide content — the data behind the hidden /how-to page.
 *
 * Written for FIRST-TIME users in plain language. Keep it in step with
 * USER-GUIDE.md (same material as a Markdown document) — see CLAUDE.md:
 * every change that alters what someone taps, sees, or may do updates both.
 *
 * Writing rules:
 *   - Exact on-screen labels in **bold**, so people can match what they see.
 *   - Short numbered steps; one action per step.
 *   - Say "you", avoid jargon; explain a term the first time or add it to
 *     HOW_TO_GLOSSARY.
 *   - Only describe what works today — no placeholders or "coming soon".
 *   - Topic ids are anchors (/how-to#<id>) people share — don't rename them.
 *   - A topic with a `video` shows a walkthrough above its steps. The video's
 *     captions are numbered "Step 1 · …" to match `steps`, so if you reorder
 *     or reword the steps, re-record the video (and vice versa).
 */
import type {
	HowToAudience,
	HowToAudienceId,
	HowToGlossaryEntry,
	HowToGroup,
	HowToTopic,
} from './howToGuide.types';

export type { HowToAudience, HowToAudienceId, HowToGlossaryEntry, HowToGroup, HowToTopic } from './howToGuide.types';

/* ============================================
 * AUDIENCES — "I am…"
 * ============================================ */

export const HOW_TO_AUDIENCES: HowToAudience[] = [
	{
		id: 'field',
		label: 'Field crew',
		headline: 'You do the work on site',
		intro: 'You get sent to jobs, work through a checklist, take photos, and sign off when it\'s done. The app shows you **only the jobs you\'re on**, so you never have to dig through anyone else\'s work.',
		covers: 'Technicians, Subcontractors, CSRs, JSRs and Other',
		roles: ['technician', 'subcontractor', 'csr', 'jsr', 'other'],
		firstDay: [
			'Sign in with the email and temporary password from your welcome email, then choose your own password.',
			'Open **Settings** and add your photo, phone number and time zone.',
			'Go to the **Dashboard** and look at **My Tasks** — that\'s your work for today.',
			'Tap a job, then tap **Start** to open the checklist.',
			'Check items off, take the photos it asks for, and finish with **Submit**.',
		],
	},
	{
		id: 'reviewer',
		label: 'Reviewer',
		headline: 'You look, you don\'t change',
		intro: 'Reviewers can see the jobs and inspections they\'re on and print reports, but can\'t change anything. It\'s the safe, read-only seat — perfect for auditing and quality checks.',
		covers: 'Reviewers',
		roles: ['reviewer'],
		firstDay: [
			'Sign in and set your own password when asked.',
			'Open **Settings** and add your details.',
			'Use **Work Orders** and **Inspections** to look up jobs you\'re on.',
			'Open any job and tap **Generate PDF** for a printable copy.',
		],
	},
	{
		id: 'manager',
		label: 'Manager',
		headline: 'You plan the work and check it',
		intro: 'Managers create work orders and inspections, assign crews, review what comes back, and approve it. You see everything for **your company**. Company Admins sit here too and also manage their company\'s users.',
		covers: 'Managers and Company Admins',
		roles: ['manager', 'company_admin'],
		firstDay: [
			'Sign in, set your password, and fill in **Settings** (time zone matters — it\'s used for scheduling).',
			'Turn on the emails you want under **Notification Controls** in **Settings**.',
			'Look over the **Dashboard** — tap any number to see the jobs behind it.',
			'Create your first job with **Work Orders → New Order**.',
			'When crews submit work, open it and choose **Approve** or **Request Changes**.',
		],
	},
	{
		id: 'admin',
		label: 'Admin',
		headline: 'You run the operation',
		intro: 'Admins do everything a manager does for their own companies (the **Primary Company** and **Additional Companies** on their user record), plus they manage people, customers, buildings, projects and repeating schedules from the **Admin** area, and the checklist templates in **CMS**.',
		covers: 'Admins',
		roles: ['admin'],
		firstDay: [
			'Sign in, set your password, and fill in **Settings**.',
			'Open **Admin** and check the **Users** and **Companies** tabs.',
			'Make sure each customer has its buildings under **Buildings**.',
			'Create a work order and try the **Notify users** prompt that appears when you save.',
			'Have a look at **Project Work Orders** and **Recurring Work Orders** in the menu.',
		],
	},
	{
		id: 'super_admin',
		label: 'Super Admin',
		headline: 'You see and control everything',
		intro: 'Super Admins have the whole platform: every company, money and billing, the checklist library (**CMS**), and the ability to sign in as someone else to help them. Developers share most of this view.',
		covers: 'Super Admins and Developers',
		roles: ['super_admin', 'developer'],
		firstDay: [
			'Sign in and set your password.',
			'Review the platform-wide **Dashboard**, including **Revenue** and **Tasks by User**.',
			'Open **CMS** and check the checklist templates, photo types, areas and business lines.',
			'Open **Admin → Billing** to see what\'s ready to invoice.',
			'Skim this guide for the other roles — you\'ll be answering their questions.',
		],
	},
];

/** Every audience id, for topics that apply to everyone. */
const ALL: HowToAudienceId[] = ['field', 'reviewer', 'manager', 'admin', 'super_admin'];
/** People who create and manage jobs. */
const OFFICE: HowToAudienceId[] = ['manager', 'admin', 'super_admin'];
/** People who run jobs on site (managers and admins step in too). */
const DOERS: HowToAudienceId[] = ['field', 'manager', 'admin', 'super_admin'];
/** Admin-area people. */
const ADMINS: HowToAudienceId[] = ['admin', 'super_admin'];

/* ============================================
 * GROUPS
 * ============================================ */

export const HOW_TO_GROUPS: HowToGroup[] = [
	{ id: 'basics', title: 'The basics', description: 'Signing in, finding your way around, and setting up your account.' },
	{ id: 'field', title: 'Doing the work', description: 'Running a job or an inspection on site, from start to sign-off.' },
	{ id: 'jobs', title: 'Creating and managing jobs', description: 'Setting up work, assigning people, and checking what comes back.' },
	{ id: 'office', title: 'Running the operation', description: 'People, customers, projects and repeating schedules.' },
	{ id: 'admin', title: 'Super Admin tools', description: 'Billing and helping other users.' },
];

/* ============================================
 * TOPICS
 * ============================================ */

/** Where topic videos live under public/ — served from the site root, so a
 *  topic's video is /videos/how-to/{desktop|mobile}/<video>.mp4 with a .jpg
 *  poster beside it. Desktop clips are landscape (recorded full screen, 720px
 *  wide); mobile clips are 540×960 portrait. */
export const HOW_TO_VIDEO_DIR = 'videos/how-to';

export const HOW_TO_TOPICS: HowToTopic[] = [
	/* ---------- The basics ---------- */
	{
		id: 'sign-in',
		group: 'basics',
		title: 'Sign in for the first time',
		summary: 'Use your welcome email to get in, then pick your own password.',
		audiences: ALL,
		steps: [
			'Find the welcome email — it has a sign-in link and a **temporary password** (it looks like *Brave-Otter-7392*).',
			'On the sign-in page, enter your **Email address** and the temporary password, then tap **Sign in**. If your company uses Microsoft, you can tap **Office 365** instead.',
			'You\'ll see **Password Change Required**. Type the temporary password as your **Current Password**, then your new one twice.',
			'Tap **Set New Password**. You\'re in.',
		],
		tips: [
			'A password needs at least 12 characters, with an uppercase letter, a lowercase letter, a number and a symbol.',
			'Forgot your password? There\'s no self-service reset yet — ask an admin to use **Reset PW** for you.',
			'CSRs and JSRs added without an email can\'t sign in — and don\'t need to. Your manager records your hours for you.',
		],
	},
	{
		id: 'get-around',
		group: 'basics',
		title: 'Find your way around',
		summary: 'Where the menu lives on a computer and on a phone.',
		audiences: ALL,
		video: 'get-around',
		steps: [
			'**On a computer or tablet**, the menu is down the left side: **Dashboard**, **Work Orders**, **Inspections**, **Buildings**, **Calendar**, **Map** and, if you have them, the admin items.',
			'**On a phone**, the menu is the bar along the bottom: **Dashboard**, **Orders**, **Inspect**, **Settings** and **More**. Tap **More** for everything else.',
			'Your name and photo are top right — tap them for **My Profile** and **Sign out**.',
			'On a big screen, use the search box at the top (**Search orders or inspections...**) to jump straight to a job.',
			'**On a phone or tablet**, a list\'s filters are tucked behind a **Filters** button so the list has room. Tap it, choose what you want to see, then tap **Show results**. The number on the button shows how many filters are on; **Reset** clears them.',
		],
		tips: ['You only see menu items your role can use, so a colleague\'s screen may look a little different from yours.'],
	},
	{
		id: 'install-app',
		group: 'basics',
		title: 'Put the app on your home screen',
		summary: 'Install it like an app so it opens full screen with one tap.',
		audiences: ALL,
		steps: [
			'Open the app in your phone\'s browser (Safari on iPhone, Chrome on Android).',
			'**iPhone:** tap the Share button, then **Add to Home Screen**. **Android:** tap the ⋮ menu, then **Install app** or **Add to Home screen**.',
			'Open it from the new GB WorkOrder icon from now on.',
		],
	},
	{
		id: 'dashboard',
		group: 'basics',
		title: 'Read your dashboard',
		summary: 'Your starting point — what\'s on your plate and how things are going.',
		audiences: ALL,
		steps: [
			'**Field crew and reviewers** see **My Tasks** (today\'s and yesterday\'s jobs) plus counts of active and completed work. Use the **All / Not Completed / Completed** chips to filter.',
			'**Managers and admins** see totals for a period — pick **This Month**, **Last 30 Days**, **Last Quarter** or **Custom** — and can switch between **My work** and **My company**.',
			'Tap any number or status bar to open the list of jobs behind it.',
			'**Super Admins** also see platform **Revenue** and a **Tasks by User** table.',
		],
	},
	{
		id: 'profile',
		group: 'basics',
		title: 'Set up your profile and emails',
		summary: 'Add your photo and details, and choose which emails you get.',
		audiences: ALL,
		steps: [
			'Open **Settings** (bottom bar on a phone, bottom of the side menu on a computer).',
			'Tap your photo circle to upload a picture, crop it, and tap **Save Photo**.',
			'Under **Personal Information**, check your name and phone, and set your **Local Timezone**. Tap **Save Changes**.',
			'Under **Notification Controls**, switch on the emails you want — for example **When a Work Order is assigned to me**. Tap **Save Preferences**.',
			'To change your password, use **Change Password** on the same page.',
		],
	},
	{
		id: 'offline',
		group: 'basics',
		title: 'Work without signal',
		summary: 'Keep going in basements and on remote sites — the app catches up later.',
		audiences: DOERS,
		steps: [
			'If you lose signal, a red bar says **You are offline. Changes will be saved locally.** Keep working.',
			'Checklist ticks and photos are saved on your phone and wait to be sent.',
			'Back in signal, a yellow bar shows how many items are waiting. They send by themselves, or tap **Sync Now**.',
			'A green **Sync complete!** means everything made it. If something fails, tap **Retry**.',
		],
		tips: [
			'Don\'t close the browser or sign out while items are still waiting — the app will warn you if you try.',
			'A few things need a connection: saving hours for someone new to the job, and creating people.',
		],
	},

	/* ---------- Doing the work ---------- */
	{
		id: 'find-your-jobs',
		group: 'field',
		title: 'Find today\'s jobs',
		summary: 'Three easy ways to see where you\'re going.',
		audiences: DOERS,
		steps: [
			'**Dashboard → My Tasks** lists today\'s and yesterday\'s jobs.',
			'**Work Orders** (**Orders** on a phone) and **Inspections** (**Inspect**) list everything you\'re on. Use the filters to narrow it down.',
			'**Calendar** shows your jobs by day, week or month; **Map** shows them as pins. Tap one, then **Directions** for turn-by-turn.',
		],
	},
	{
		id: 'start-work-order',
		group: 'field',
		title: 'Start a work order',
		summary: 'Open the job and step into the checklist.',
		audiences: DOERS,
		steps: [
			'Find the job and tap **Start** (or **Resume** if you\'ve already begun).',
			'The job opens full screen with three steps along the top: **Checklist**, **Verify**, **Sign Off**.',
			'Tap the people icon (**Job details**) any time to see the address, **Directions**, the customer contact and the schedule, and to leave **Job Notes** for the office.',
			'Tap the **X** to step out — your progress is kept.',
		],
	},
	{
		id: 'checklist-photos',
		group: 'field',
		title: 'Check off items and take photos',
		summary: 'Work down the list; the app tells you which photos it needs.',
		audiences: DOERS,
		steps: [
			'Tap an item to check it off. Items marked **Required** must be done before you can finish.',
			'If an item needs a photo, tap **Take photo for this item** (or **Take BEFORE photo**, then **Take AFTER photo** later).',
			'Take the picture, add a **Title** and **Area** if asked, and tap **Save Photo**. It uploads in the background — you can keep working.',
			'Found extra work that isn\'t on the list? Tap **Add item**, type it (e.g. "Rooms 201-220") and save.',
			'The counter shows how many items are left.',
		],
		tips: ['An item that needs a photo can\'t be checked off until the photo is taken.', 'If a photo fails to upload, tap **Retry all** on the bar that appears.'],
	},
	{
		id: 'sign-off',
		group: 'field',
		title: 'Finish and sign off a work order',
		summary: 'Verify, record your hours, sign, and submit.',
		audiences: DOERS,
		steps: [
			'When every item is done, tap **Continue to Verification**.',
			'Add any **Verification Photos**, then tap **Verify & Continue to Sign Off**.',
			'Under **Hours Worked**, enter how long each person on the crew spent on the job.',
			'Sign in the **Your Signature** box with your finger, then tap **Accept Signature**.',
			'Add **Job Notes** if there\'s anything the office should know, then tap **Submit Work Order**.',
		],
		tips: [
			'If the job **requires hours**, you can\'t submit until everyone has a number.',
			'The total is shown against the estimate, so you\'ll see if the job ran over or under.',
		],
	},
	{
		id: 'submit-for-review',
		group: 'field',
		title: 'Submit when the customer signs separately',
		summary: 'When someone else (like the property manager) signs off, you hand the job over.',
		audiences: DOERS,
		steps: [
			'When every item is done, tap **Submit**.',
			'In **Submit work order**, fill in **How long did this take?** for each person.',
			'If the customer contact is listed, choose **Email & Submit** to email them a request to verify and sign, or **Don\'t Email** to skip it.',
			'Your manager is told either way. Tap **Done**.',
		],
	},
	{
		id: 'helping-on-a-job',
		group: 'field',
		title: 'Help on someone else\'s job',
		summary: 'When you\'re extra crew rather than the lead.',
		audiences: ['field'],
		steps: [
			'You can open the job and check off items just like the lead tech.',
			'When everything\'s done you\'ll see **The primary technician will verify and sign off.** — let them know you\'re finished.',
			'The lead records your hours when they sign off.',
		],
	},
	{
		id: 'run-inspection',
		group: 'field',
		title: 'Do an inspection',
		summary: 'Rate each area, photograph what you find, and submit.',
		audiences: DOERS,
		video: 'run-inspection',
		steps: [
			'Open the inspection and tap **Start Inspection** (on a list, tap **Start**).',
			'For each item, pick a rating: **Good** (nothing to do), **Needs Attention** (follow up in 1–2 weeks) or **Critical** (needs action now).',
			'A **Critical** rating needs a short note explaining the problem — it\'s emailed to management straight away.',
			'Every item has a camera button on its right — tap it to add a photo right then, and tag it with the **Area** it shows. Use **Add item** for anything not on the list (e.g. "East stairwell").',
			'When every item is rated, tap **Continue to Verification**.',
			'Check your ratings, add any **Observation Photos**, and tap **Continue to Sign Off**.',
			'Write your recommendations and tap **Submit Inspection**.',
		],
		tips: [
			'Critical items that aren\'t sorted within about a day are escalated to the branch manager automatically.',
			'Inspections never ask for a signature — only work orders are signed by the customer.',
			'Closed it halfway? Your ratings and notes are saved. Open the inspection again and tap **Resume Workflow** to pick up where you left off.',
		],
	},
	{
		id: 'look-up-reports',
		group: 'field',
		title: 'Look up a job and print a report',
		summary: 'See the full record and get a PDF.',
		audiences: ALL,
		steps: [
			'Open **Work Orders** or **Inspections** and tap the job.',
			'The **Details** tab has everything about it; **Photos** shows before and after pictures once there are some.',
			'Tap **Generate PDF** for a printable copy you can save or email.',
		],
	},

	/* ---------- Creating and managing jobs ---------- */
	{
		id: 'create-work-order',
		group: 'jobs',
		title: 'Create a work order',
		summary: 'Set up a job: where, what, who and when.',
		audiences: ['field', 'manager', 'admin', 'super_admin'],
		video: 'create-work-order',
		steps: [
			'Go to **Work Orders** and tap **New Order**.',
			'**Project Information:** give it a name and description, and choose the **Business Line** and **Sub Type** (these can pre-fill the checklist).',
			'**Customer & Location:** pick the **Company**, then the **Building** (missing? tap **+ Add a New Building**). Under **Customer Contact**, pick who on site will sign off — an existing contact, a user, or a **New Contact**.',
			'**Assignment:** choose **Assign To** and any **Additional Assignees**, the **Priority**, **Scheduled Date** and times. Someone missing? Tap **+ Quick Add User**.',
			'**Billing:** tick the billing tags (e.g. **Billable**, **T&M**) and check the billing details.',
			'**Settings:** pick the checklist, decide if a **Signature Required**, add an estimate if you have one, and set **Frequency** if it repeats.',
			'Tap **Create Work Order**.',
		],
		tips: [
			'Technicians, Managers, Admins and Super Admins can create work orders. Subcontractors, CSRs, JSRs, Reviewers and Company Admins can\'t — ask a manager or admin.',
			'To make a similar job quickly, open one and choose **Duplicate**.',
		],
	},
	{
		id: 'assign-work-order',
		group: 'jobs',
		title: 'Assign or reassign a work order',
		summary: 'Put a crew on a job, or change who\'s on it, straight from the Work Orders list.',
		audiences: OFFICE,
		video: 'assign-work-order',
		steps: [
			'Go to **Work Orders**. In the **Assignee** column, tap **Assign** on a job nobody is on yet, or tap the person\'s name to change it. Each row\'s **⋯** menu has the same **Assign** / **Reassign** option.',
			'The **Assign Work Order** window shows only the assignment: **Assign To**, **Additional Assignees**, **Priority**, **Scheduled Date**, the start and end times, and the **Timezone**.',
			'Choose who does it under **Assign To**. Tap **Add Assignee** to add more people, or the **×** on a name to take them off. Not in the system yet? Tap **+ Quick Add User**.',
			'Tap **Save Assignment**. Admins are asked who should get an email first.',
		],
		tips: [
			'On an hours job everyone gets an hours row — type each person\'s hours before you save.',
			'No **Assign** button? It comes with the **Can Reassign Work Orders** permission — ask an admin to turn it on for you.',
			'Close the window with unsaved changes and it asks before throwing them away.',
		],
	},
	{
		id: 'require-hours',
		group: 'jobs',
		title: 'Track hours on a job',
		summary: 'Give each person an hours row and set an hours budget.',
		audiences: OFFICE,
		steps: [
			'On the work order form, tick **Require Hours** (it\'s on automatically for **T&M** jobs).',
			'Enter the hours budget for the job (the **bucket**) — required for T&M.',
			'The assignee pickers turn into rows: pick a person with **+ Add assignee** and type their hours.',
			'The **Total Hours** line shows how much of the bucket is used and warns if you go over.',
		],
		tips: ['Every person on an hours job needs hours before it can be submitted, approved or completed.'],
	},
	{
		id: 'quick-add-user',
		group: 'jobs',
		title: 'Add a person who isn\'t in the system yet',
		summary: 'Use **+ Quick Add User** right where you pick people — assignees, contacts or hours.',
		audiences: OFFICE,
		steps: [
			'Look for **+ Quick Add User** wherever you choose people: next to **Add Assignee** on the work order form or in the **Assign Work Order** window, under **Select User** or **Existing Contact** in Customer Contact, under the hours rows, in **Job details**, or in the **Hours Worked** step.',
			'It\'s also the last row of the drop-down list. Searched for someone and got no results? Tap it — the name you typed is filled in for you.',
			'Choose the type: **CSR** (the default), **JSR**, or **Full User**.',
			'For a CSR or JSR, just enter **First name**, **Last name** and **Primary business**. Email, phone and password are optional.',
			'For a **Full User** (someone who will sign in), also enter **Email**, **Phone** and a **Temporary password** — one is filled in for you. They start as a Technician and get a welcome email.',
			'**Primary business** is already set to the work order\'s customer. Type to search if it should be different.',
			'Adding several people? Tap **+ Add another person** for each one.',
			'Tap **Add person** (or **Add 3 people**). They appear straight away where you started: as the assignee, on the crew, as the customer contact, or as new hours rows ready for numbers.',
		],
		tips: [
			'When you\'re picking a customer contact, you add one person at a time and their business is set to that customer.',
			'From **Assign To**, the new person becomes the main assignee. From **Additional Assignees**, they join the crew.',
			'Managers can add **Full Users** only; adding CSRs and JSRs is for admins.',
			'If one person can\'t be saved (say, the email is already used), everyone else is still added and only that person stays on screen with the reason.',
			'The summary shows any temporary passwords with a **Copy** button so you can pass them on.',
		],
	},
	{
		id: 'notify-users',
		group: 'jobs',
		title: 'Choose who gets emailed when you save',
		summary: 'Admins see exactly who a save will email — and can untick anyone.',
		audiences: ADMINS,
		steps: [
			'Save a work order or inspection as usual.',
			'If the save would send emails, a list of recipients appears, each with a checkbox.',
			'Untick anyone who shouldn\'t hear about it (or use **Uncheck everyone**).',
			'Tap **Email & Save**, **Don\'t Email Anyone**, or **Save Without Emailing**.',
		],
	},
	{
		id: 'review-approve',
		group: 'jobs',
		title: 'Review and approve finished work',
		summary: 'Check what the crew submitted and approve it or send it back.',
		audiences: OFFICE,
		steps: [
			'Submitted jobs show as **In Review**. Find them from the dashboard or the **Order Status** filter.',
			'Open the job and look over the checklist, photos and hours.',
			'Tap **Approve** if it\'s good, or **Request Changes** to send it back to the crew.',
			'Once approved, tap **Complete** to close out the work.',
		],
		tips: [
			'You can\'t approve your own finished work — another supervisor has to.',
			'Need the customer to sign? Tap **Email Contact to Verify** to send them a request.',
		],
	},
	{
		id: 'approve-hours',
		group: 'jobs',
		title: 'Approve hours and estimates',
		summary: 'Hours and estimates need a yes before billing.',
		audiences: OFFICE,
		steps: [
			'Open the work order and find **Hours** on the **Details** tab.',
			'Admins tap **Approve as Admin**. Or tap **Customer Approval** to record that the customer\'s main contact agreed.',
			'For an estimate, a Super Admin taps **Approve as Super Admin**, or choose a contact under customer approval to record their sign-off in person.',
			'Made a mistake? Use **Revoke**.',
		],
		tips: ['A job with unapproved hours can\'t be invoiced.'],
	},
	{
		id: 'create-inspection',
		group: 'jobs',
		title: 'Create an inspection',
		summary: 'Schedule a quality check of a building.',
		audiences: OFFICE,
		steps: [
			'Go to **Inspections** and tap **New Inspection**.',
			'Pick the **Company** and **Building**, choose the inspection checklist, and set the **Inspection Date**.',
			'Assign who will do it. You\'re recorded as the **Inspector**.',
			'Tick **Send to Client** only if the customer should be emailed the report when it\'s finished — it\'s off by default so you can review first.',
			'Save it.',
		],
	},
	{
		id: 'finish-inspection',
		group: 'jobs',
		title: 'Close out an inspection and follow up',
		summary: 'Review the findings, finish it, and turn problems into work.',
		audiences: OFFICE,
		steps: [
			'Submitted inspections show as **Pending Review**. Open one and check the **Inspection Ratings** panel.',
			'Tap **Complete Inspection** when you\'re happy with it.',
			'For anything that failed, tap **Create Follow-Up** — it starts a work order with the customer already filled in.',
		],
	},
	{
		id: 'edit-duplicate',
		group: 'jobs',
		title: 'Edit, duplicate or cancel a job',
		summary: 'Change details or reuse a job as a template.',
		audiences: OFFICE,
		steps: [
			'Open the job. Tap **Edit** to change it, then save.',
			'From the **⋯** menu choose **Duplicate** to start a copy, **Create Follow-Up** for related work, or **Cancel** to call it off.',
			'Use **Put On Hold** / **Resume Work** to pause a job without losing it.',
		],
		tips: [
			'**Assign To** greyed out? Moving a job to someone else needs the **Can Reassign Work Orders** permission. Everything else still saves.',
		],
	},
	{
		id: 'buildings',
		group: 'jobs',
		title: 'Add and manage buildings',
		summary: 'Every job happens at a building that belongs to a customer.',
		audiences: OFFICE,
		steps: [
			'Open **Buildings** and tap **Add Building**.',
			'Start typing the address and pick it from the suggestions, then choose the customer it belongs to.',
			'Add the business line, cleanable square feet and any notes, and save.',
		],
		tips: ['You can also add a building in the middle of creating a job with **+ Add a New Building**.'],
	},
	{
		id: 'add-user',
		group: 'jobs',
		title: 'Add a new user',
		summary: 'Give someone an account from anywhere in the app.',
		audiences: OFFICE,
		video: 'add-user',
		steps: [
			'Tap **Add User** at the top of the screen.',
			'Enter their name, email and phone, tick their role(s), and pick their **Primary business**.',
			'Adding a subcontractor? Tick **Subcontractor** and type the business they work for in **Subcontractor company** (e.g. "Cyclone Window Cleaning"). Keep their own name in the name boxes.',
			'A **Temporary password** is made for you. Tap **Create user** — they\'re emailed a sign-in link.',
		],
		tips: [
			'A subcontractor shows up in pickers as **Name — Business (Subcontractor)**, and you can search for them by their business name.',
			'**Reviewer** is ticked by default because it\'s the safest, read-only role. Change it if they need more.',
			'Managers can add Technicians, Subcontractors and Reviewers. Company Admins can also add Company Admins.',
		],
	},

	/* ---------- Running the operation ---------- */
	{
		id: 'company-admin-users',
		group: 'office',
		title: 'Manage your company\'s users (Company Admins)',
		summary: 'Company Admins look after the people in their own company.',
		audiences: ['manager'],
		steps: [
			'Go to **/admin** in your browser\'s address bar (it\'s not in the menu for Company Admins yet).',
			'The **Users** tab shows people in your company. Add, edit or deactivate them there.',
		],
	},
	{
		id: 'admin-users',
		group: 'office',
		title: 'Manage users',
		summary: 'Add, change, deactivate and help people.',
		audiences: ADMINS,
		steps: [
			'Open **Admin → Users**.',
			'Tap **Add User** to create someone; tap a row to edit their roles, businesses and permissions (**Can View Financials**, **Can Reassign Work Orders**).',
			'Use **Reset PW** when someone forgets their password — they\'ll choose a new one at next sign-in.',
			'**Deactivate** someone who has left. Their history stays.',
		],
		tips: [
			'**Can Reassign Work Orders** lets someone move a job to a different person, from the Work Orders list or the edit form. Turn it on for everyone who dispatches crews.',
		],
	},
	{
		id: 'delete-job',
		group: 'office',
		title: 'Delete an old work order or inspection',
		summary: 'Clear finished or mistaken jobs out of your lists.',
		audiences: OFFICE,
		video: 'delete-job',
		steps: [
			'In **Work Orders** or **Inspections**, tap the **⋯** menu on the job\'s row and choose **Delete** — or open the job and choose **Delete** from its **⋯** menu.',
			'Check the name in the pop-up and tap **Delete**.',
		],
		tips: [
			'Managers, Admins and Super Admins can delete. A deleted job disappears from every list, but it isn\'t wiped — ask a developer if one needs bringing back.',
			'To stop a whole repeating schedule, open it under **Recurring Work Orders** and tap **Cancel Series** instead.',
		],
	},
	{
		id: 'who-can-do-what',
		group: 'office',
		title: 'Who can do what (roles)',
		summary: 'Pick the right role when you set someone up.',
		audiences: ADMINS,
		steps: [
			'**Super Admin:** everything — every company, the whole **CMS**, invoicing in **Admin → Billing**, and **Sign In As** to help someone.',
			'**Admin:** runs the operation for their own companies — every **Admin** tab except **Billing**, plus **CMS → Checklists** and **Areas**. Creates and approves work orders and inspections. A company they add goes on their list.',
			'**Manager:** creates, assigns and approves work orders and inspections, and can add Technicians, Subcontractors and Reviewers.',
			'**Technician:** works the jobs they\'re on, and can create a new work order (for example, extra work spotted on site).',
			'**Subcontractor, CSR, JSR, Other:** see and work only the jobs they\'re put on.',
			'**Reviewer:** read-only — can look but not change anything.',
			'**Can View Financials** (at the bottom of the user form) shows the **Billing** tab with the estimate on work orders. Leave it off for anyone who shouldn\'t see pricing.',
			'**Can Reassign Work Orders** (beside it) lets someone move a job to a different person — the **Assign** button on the Work Orders list, and **Assign To** when editing. Turn it on for everyone who dispatches crews. Without it they can still edit the rest of a job, and add helpers and hours from the field.',
		],
		tips: [
			'Someone can hold more than one role — they get everything each role allows.',
			'Only Super Admins and Developers see every company. Admins, Managers and Company Admins see the companies on their user record (**Primary Company** and **Additional Companies**). To give someone another company, add it under **Additional Companies**.',
		],
	},
	{
		id: 'customers-contacts',
		group: 'office',
		title: 'Manage customers and contacts',
		summary: 'Keep companies, their contacts and their buildings tidy.',
		audiences: ADMINS,
		steps: [
			'**Admin → Companies:** add or edit customers with **Add Company**.',
			'**Admin → Contacts:** add the people who sign off on site with **Add Contact**.',
			'Lots to add? Use **Import CSV**: download the template, fill it in, upload it, check the column matching, and tap **Import**.',
			'Using HubSpot? **Admin → Integrations** connects it so contacts and companies can be imported or exported.',
		],
	},
	{
		id: 'projects',
		group: 'office',
		title: 'Run a project with several work orders',
		summary: 'Project Work Orders share one pool of hours across many jobs.',
		audiences: ADMINS,
		steps: [
			'Open **Project Work Orders** and tap **+ New Project**.',
			'Set the customer, building, billing and the total hours for the project.',
			'On the project, tap **+ Add Work Order** for each piece of work. They inherit the customer, building and billing.',
			'The project page shows hours **Allocated / Total** and whether each job came in over or under.',
		],
	},
	{
		id: 'recurring',
		group: 'office',
		title: 'Set up repeating jobs',
		summary: 'Create a job once and let it repeat on a schedule.',
		audiences: ADMINS,
		video: 'recurring',
		steps: [
			'Create a work order or inspection as normal.',
			'Under **Settings → Frequency**, choose a pattern such as **Weekly on Monday**, **Annually on …** or **Custom**. It repeats with no end date unless you set one under **Custom → Ends**.',
			'Save. Each job is created on its own date, so your job list only shows work that\'s due. **Recurring Work Orders** or **Recurring Inspections** shows every series and its **Next job** date.',
			'Open a series to move or cancel a single date, or tap **Cancel Series** to stop them all.',
		],
	},

	/* ---------- Super Admin tools ---------- */
	{
		id: 'billing-queue',
		group: 'admin',
		title: 'Invoice finished work',
		summary: 'Work down the billing queue and mark jobs invoiced.',
		audiences: ['super_admin'],
		steps: [
			'Open **Admin → Billing**.',
			'**Ready to Invoice** lists completed, billable work. Filter by **Customers**, **Business Lines**, **Billing Tags** or completion dates.',
			'Tick the jobs you\'ve invoiced and tap **Mark Invoiced**.',
			'Later, close them from the **Invoiced** stage.',
		],
		tips: ['Jobs flagged **Hours not approved** can\'t be selected until their hours are approved.', 'Only Super Admins can invoice or close.'],
	},
	{
		id: 'cms',
		/* 'office', not 'admin' ("Super Admin tools"): Admins use the CMS too
		   since Oct 2026, so it belongs with the other running-the-operation
		   topics they see. */
		group: 'office',
		title: 'Manage checklists, photo types and business lines',
		summary: 'The CMS is the library every job is built from.',
		audiences: ['admin', 'super_admin'],
		video: 'cms',
		steps: [
			'Open **CMS**. Admins see the **Checklists** and **Areas** tabs; Super Admins see every tab.',
			'**Checklists:** switch to **Work Order Templates** or **Inspection Templates** and tap **New … Template** (or **Edit** / **Duplicate** on an existing one).',
			'Give it a title and add the items with **+ Add Item**. Use **Default For** to make it the automatic pick for a business line and sub type.',
			'Tap **Create Checklist** (or **Save**). Saving makes a new version, so jobs already underway don\'t change.',
			'**Areas** are what inspectors tag photos with — one list used on every inspection for every customer. Type a name and tap **Add area**. **Photo Types** are set per customer.',
			'**Business Lines:** add lines and sub types, or switch them off with **Active**.',
		],
		tips: ['**Photo Types** and **Business Lines** are Super Admin only.'],
	},
	{
		id: 'sign-in-as',
		group: 'admin',
		title: 'See the app as someone else',
		summary: 'Sign in as a user to see exactly what they see.',
		audiences: ['super_admin'],
		steps: [
			'Open **Admin → Users** and choose **Sign In As** on their row.',
			'A banner shows **Viewing as …** while you\'re them.',
			'Tap **Stop Impersonation** to go back to your own account.',
		],
		tips: ['Signing in as someone who hasn\'t chosen their own password yet takes you straight in. They\'re still asked to choose one when they sign in themselves.'],
	},
];

/* ============================================
 * GLOSSARY
 * ============================================ */

export const HOW_TO_GLOSSARY: HowToGlossaryEntry[] = [
	{ term: 'Work order', meaning: 'A job to be done at a building — cleaning, construction work, repairs.' },
	{ term: 'Inspection', meaning: 'A quality check of a building, where each area is rated.' },
	{ term: 'Pending', meaning: 'Scheduled, not started yet.' },
	{ term: 'In Progress', meaning: 'Someone has started working on it.' },
	{ term: 'In Review', meaning: 'The crew has submitted it; a manager needs to check it.' },
	{ term: 'Approved', meaning: 'A manager has checked the work and is happy with it.' },
	{ term: 'On Hold', meaning: 'Paused for now. **Resume Work** picks it back up.' },
	{ term: 'Completed', meaning: 'The work is done and signed off.' },
	{ term: 'Invoiced / Closed', meaning: 'The customer has been billed / the job is fully finished. Super Admin only.' },
	{ term: 'Pending Review', meaning: 'An inspection that has been submitted and is waiting for a manager.' },
	{ term: 'Good / Needs Attention / Critical', meaning: 'Inspection ratings: fine / follow up in 1–2 weeks / needs action now (emails management).' },
	{ term: 'Checklist', meaning: 'The list of tasks for a job, with any photos each one needs.' },
	{ term: 'Primary tech', meaning: 'The lead person on a job — the one who signs it off.' },
	{ term: 'Customer contact', meaning: 'The person at the customer who can verify and sign for the work.' },
	{ term: 'Require Hours / bucket', meaning: 'A job that tracks each person\'s hours against a budget (the bucket).' },
	{ term: 'T&M', meaning: 'Time & Materials — billed by the hour, so hours are always tracked.' },
	{ term: 'CSR / JSR', meaning: 'Construction / Janitorial Service Rep — crew who can be assigned and given hours using just a name.' },
	{ term: 'Full User', meaning: 'Someone with their own sign-in (starts as a Technician).' },
	{ term: 'Business line / Sub type', meaning: 'The kind of work — e.g. Janitorial or Construction, then Rough Clean, Final Clean, T&M…' },
	{ term: 'Follow-up', meaning: 'A new work order created to fix something found on a job or inspection.' },
];

/* ============================================
 * HELPERS
 * ============================================ */

/** Audiences from most to least powerful — a person with several roles gets the strongest. */
const AUDIENCE_PRECEDENCE: HowToAudienceId[] = ['super_admin', 'admin', 'manager', 'reviewer', 'field'];

/** The audience a signed-in user should land on, from their role slugs. Field crew when unknown. */
export function audienceForRoles(roles: readonly string[]): HowToAudienceId {
	for (const id of AUDIENCE_PRECEDENCE) {
		const audience = HOW_TO_AUDIENCES.find((a) => a.id === id);
		if (audience && audience.roles.some((r) => roles.includes(r))) return id;
	}
	return 'field';
}

/** Topics written for an audience, in guide order. */
export function topicsForAudience(id: HowToAudienceId): HowToTopic[] {
	return HOW_TO_TOPICS.filter((t) => t.audiences.includes(id));
}

/** Case-insensitive match of a search query against a topic's words. Empty query matches all. */
export function topicMatches(topic: HowToTopic, query: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	const haystack = [topic.title, topic.summary, ...topic.steps, ...(topic.tips ?? [])].join(' ').toLowerCase().replace(/\*\*/g, '');
	return q.split(/\s+/).every((word) => haystack.includes(word));
}
